import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target,
  Brain,
  TrendingUp,
  ChevronRight,
  Sun,
  Moon,
  ArrowRight,
  Activity,
  Database,
  FileText,
  BarChart3,
  ClipboardCheck,
  Search,
  Users,
  Building2,
  Shield,
  Settings,
  HeadphonesIcon,
  GraduationCap,
  BookOpen,
  LineChart,
  CheckCircle2,
  CalendarCheck,
  Clock,
  ClipboardList,
  Menu,
  X,
} from 'lucide-react';
import DotGrid from '../components/home/DotGrid';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0 }
};

const slideInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function Homepage() {
  const [dark, setDark] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const bg = dark ? 'bg-[#0D0D0D]' : 'bg-white';
  const text = dark ? 'text-gray-100' : 'text-gray-800';
  const textMuted = dark ? 'text-gray-400' : 'text-gray-500';
  const textSubtle = dark ? 'text-gray-300' : 'text-gray-600';
  const cardBg = dark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200';
  const cardHover = dark ? 'hover:bg-white/10' : 'hover:shadow-lg hover:border-sideb-blue/30';
  const sectionAlt = dark ? 'bg-white/[0.02]' : 'bg-gray-50';
  const divider = dark ? 'border-white/10' : 'border-gray-200';

  const colors = {
    blue: '#0974BA',
    green: '#13983D',
    yellow: '#F9B047',
    red: '#E83A3C',
    navy: '#2E4974',
  };

  const sistemComponents = [
    {
      icon: <ClipboardCheck size={28} />,
      color: colors.blue,
      title: 'Avaliação Diagnóstica',
      desc: 'Criação e aplicação de instrumentos avaliativos estruturados por habilidade e nível de dificuldade.',
    },
    {
      icon: <Database size={28} />,
      color: colors.green,
      title: 'Banco de Itens',
      desc: 'Base de questões classificadas por competência, habilidade e descritor pedagógico.',
    },
    {
      icon: <BarChart3 size={28} />,
      color: colors.yellow,
      title: 'Painéis Gerenciais',
      desc: 'Visualização de indicadores consolidados por aluno, turma, escola e rede.',
    },
    {
      icon: <FileText size={28} />,
      color: colors.red,
      title: 'Relatórios Técnicos',
      desc: 'Geração automatizada de relatórios analíticos para apoio à gestão.',
    },
    {
      icon: <Activity size={28} />,
      color: colors.navy,
      title: 'Monitoramento de Aplicação',
      desc: 'Acompanhamento de participação, execução e resultados de avaliações.',
    },
    {
      icon: <Search size={28} />,
      color: colors.blue,
      title: 'Análise de Respostas',
      desc: 'Análise de alternativas selecionadas para identificação de padrões de erro.',
    },
    {
      icon: <CalendarCheck size={28} />,
      color: colors.green,
      title: 'Diário Digital',
      desc: 'Registro de aula, conteúdo e BNCC, com frequência lançada por aula — e não pelo dia inteiro. Permite saber em qual aula o aluno faltou.',
    },
    {
      icon: <Clock size={28} />,
      color: colors.yellow,
      title: 'Grade Horária',
      desc: 'O quadro de aulas de cada turma, com vigência por período. É dela que sai a lista de aulas do professor e a previsão que a coordenação cobra.',
    },
    {
      icon: <ClipboardList size={28} />,
      color: colors.red,
      title: 'Acompanhamento de Diários',
      desc: 'Aulas previstas contra aulas lançadas, dia a dia, por escola, turma e professor. Pendências em lista e em planilha, prontas para cobrança.',
    },
  ];

  const gestaoAplicacoes = [
    { icon: <Target size={20} />, text: 'Planejamento pedagógico baseado em evidência' },
    { icon: <TrendingUp size={20} />, text: 'Priorização de ações de reforço' },
    { icon: <BookOpen size={20} />, text: 'Acompanhamento de programas educacionais' },
    { icon: <LineChart size={20} />, text: 'Monitoramento de desempenho por unidade' },
    { icon: <Users size={20} />, text: 'Identificação de grupos de risco' },
    { icon: <GraduationCap size={20} />, text: 'Apoio à coordenação pedagógica' },
  ];

  const funcionalidadesTecnicas = [
    'Classificação de itens por habilidade e matriz pedagógica',
    'Análise de desempenho por competência avaliada',
    'Indicadores comparativos por período',
    'Visualização gráfica de evolução',
    'Frequência lançada por aula, com apuração automática do dia',
    'Registro de substituição: a aula coberta credita o horário do professor ausente',
    'Calendário letivo por rede e por escola, com bloqueio de lançamento em dia sem aula',
    'Projeção de metas de desempenho',
    'Exportação de relatórios e de pendências de diário',
    'Consolidação de dados por unidade escolar',
  ];

  const beneficiosAdmin = [
    'Decisão baseada em dados estruturados',
    'Padronização de instrumentos avaliativos',
    'Melhor direcionamento de recursos pedagógicos',
    'Transparência de indicadores',
    'Documentação técnica de resultados',
    'Acompanhamento contínuo de evolução',
  ];

  const governanca = [
    'Controle de acesso por perfil de usuário',
    'Rastreabilidade de aplicações e resultados',
    'Registro estruturado de dados',
    'Relatórios auditáveis',
    'Boas práticas de proteção de dados',
  ];

  const implantacao = [
    'Configuração inicial da rede de ensino',
    'Capacitação de gestores e equipes',
    'Suporte técnico operacional',
    'Acompanhamento de implantação',
    'Orientação de uso pedagógico',
  ];

  const publicoAlvo = [
    { icon: <Building2 size={24} />, text: 'Secretarias Municipais e Estaduais de Educação' },
    { icon: <Users size={24} />, text: 'Equipes de gestão pedagógica' },
    { icon: <GraduationCap size={24} />, text: 'Diretores e coordenadores escolares' },
    { icon: <BarChart3 size={24} />, text: 'Setores de avaliação e monitoramento' },
  ];

  const navLinks = [
    { href: '#finalidade', label: 'Finalidade' },
    { href: '#componentes', label: 'Componentes' },
    { href: '#funcionalidades', label: 'Funcionalidades' },
    { href: '#sobre', label: 'Sobre' },
  ];

  return (
    <div className={`min-h-screen ${bg} ${text} transition-colors duration-500 overflow-x-hidden`}>
      {/* ===== NAV ===== */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl ${dark ? 'bg-[#0D0D0D]/80 border-white/10' : 'bg-white/80 border-gray-200'} border-b`}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <motion.div
            className="flex items-center gap-3"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <img src="/LogoNome.svg" alt="SIDEB+" className="h-9" />
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8 text-sm font-medium">
            {navLinks.map((link, i) => (
              <motion.a
                key={link.href}
                href={link.href}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * i }}
                className={`${textMuted} hover:text-[#0974BA] transition relative group`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#0974BA] transition-all group-hover:w-full" />
              </motion.a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.1, rotate: 180 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.3 }}
              onClick={() => setDark(!dark)}
              className={`p-2 rounded-full ${dark ? 'bg-white/10 text-yellow-400' : 'bg-gray-100 text-gray-600'} transition`}
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/login')}
              className="hidden sm:flex px-5 py-2 rounded-full text-sm font-semibold text-white transition"
              style={{ background: colors.blue }}
            >
              Entrar
            </motion.button>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`md:hidden border-t ${divider} ${dark ? 'bg-[#0D0D0D]' : 'bg-white'}`}
            >
              <div className="px-6 py-4 space-y-3">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block py-2 ${textMuted} hover:text-[#0974BA] transition`}
                  >
                    {link.label}
                  </a>
                ))}
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-2 rounded-full text-sm font-semibold text-white mt-4"
                  style={{ background: colors.blue }}
                >
                  Entrar
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* ===== HERO SECTION COM DOTGRID ===== */}
      <div className="relative">
        <div className="sticky top-0 h-screen w-full overflow-hidden z-0 pointer-events-none md:pointer-events-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 z-10"
            style={{
              background: dark
                ? 'radial-gradient(circle, transparent 20%, #0D0D0D 85%)'
                : 'radial-gradient(circle, transparent 20%, #ffffff 85%)'
            }}
          />
          <div className="absolute inset-0 w-full h-full opacity-60">
            <DotGrid
              dotSize={6}
              gap={24}
              proximity={150}
              speedTrigger={150}
              shockRadius={200}
              shockStrength={10}
              returnDuration={2}
            />
          </div>
        </div>

        <div className="relative z-20" style={{ marginTop: '-100vh' }}>
          {/* HERO */}
          <section className="relative min-h-screen flex flex-col justify-center pt-32 pb-20 px-6">
            <div className="max-w-5xl mx-auto text-center relative z-10">
              <motion.div
                className="flex justify-center mb-8"
                initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
              >
                {/* Logo muda baseado no tema */}
                <motion.img
                  key={dark ? 'dark' : 'light'}
                  src={"/MaisColorido.svg"}
                  alt="SIDEB+"
                  className="h-24 md:h-32"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                />
              </motion.div>

              <motion.h1
                className="text-3xl sm:text-4xl lg:text-6xl font-black leading-tight tracking-tight mb-6 drop-shadow-sm"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Plataforma de Monitoramento e{' '}
                <motion.span
                  className="bg-clip-text text-transparent inline-block"
                  style={{ backgroundImage: `linear-gradient(135deg, #0974BA, #2E4974)` }}
                  animate={{
                    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                  }}
                  transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                >
                  Gestão de Indicadores
                </motion.span>{' '}
                Educacionais
              </motion.h1>

              <motion.p
                className={`font-medium text-lg sm:text-xl max-w-4xl mx-auto mb-10 leading-relaxed ${textSubtle}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Solução tecnológica desenvolvida para apoiar redes públicas de ensino no acompanhamento contínuo de desempenho acadêmico, frequência, habilidades avaliadas e evolução pedagógica por meio de dados estruturados.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(9,116,186,0.3)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/login')}
                  className="px-8 py-3.5 rounded-full text-white font-bold text-base flex items-center gap-2 shadow-xl transition"
                  style={{ background: `linear-gradient(135deg, #0974BA, #2E4974)` }}
                >
                  Acessar o Sistema
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight size={18} />
                  </motion.span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(249, 176, 71,0.3)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/demo')}
                  className="px-8 py-3.5 rounded-full text- font-bold text-base flex items-center gap-2 shadow-xl transition"
                  style={{ background: `linear-gradient(135deg,#f5ba64, #F9B047)` }}
                >
                  Ver Demo
                  <BarChart3 size={18} />
                </motion.button>
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="#finalidade"
                  className={`px-8 py-3.5 rounded-full font-bold text-base border flex items-center gap-2 transition backdrop-blur-md ${dark ? 'bg-white/5 border-white/20 text-gray-300' : 'bg-white/60 border-gray-300 text-gray-600'}`}
                >
                  Saiba Mais
                  <ChevronRight size={18} />
                </motion.a>
              </motion.div>

              {/* Stats bar */}
              <motion.div
                className={`max-w-4xl mx-auto mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 p-6 rounded-2xl border backdrop-blur-md ${dark ? 'bg-black/40 border-white/10' : 'bg-white/60 border-gray-200 shadow-xl'}`}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                {[
                  { value: '500+', label: 'Escolas atendidas', color: colors.blue },
                  { value: '120k+', label: 'Alunos avaliados', color: colors.green },
                  { value: '2M+', label: 'Questões processadas', color: colors.yellow },
                  { value: '98%', label: 'Satisfação', color: colors.red },
                ].map((s, i) => (
                  <motion.div
                    key={i}
                    className="text-center"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1 + (i * 0.1), type: "spring" }}
                  >
                    <motion.div
                      className="text-2xl sm:text-3xl font-black"
                      style={{ color: s.color }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {s.value}
                    </motion.div>
                    <div className={`text-sm font-bold mt-1 ${textMuted}`}>{s.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
        </div>
      </div>

      {/* ===== FINALIDADE ===== */}
      <section id="finalidade" className={`py-20 px-6 ${sectionAlt}`}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: colors.blue }}>
              Finalidade da Solução
            </p>
            <h2 className="text-3xl sm:text-4xl font-black mb-6">
              Instrumentos Técnicos para Gestão Educacional
            </h2>
            <p className={`max-w-3xl mx-auto ${textSubtle} text-lg`}>
              A plataforma foi estruturada para fornecer instrumentos técnicos de apoio à gestão educacional, permitindo:
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {[
              { icon: <Activity size={24} />, text: 'Monitoramento contínuo de desempenho escolar', color: colors.blue },
              { icon: <ClipboardCheck size={24} />, text: 'Aplicação de avaliações diagnósticas', color: colors.green },
              { icon: <Brain size={24} />, text: 'Análise de habilidades e competências', color: colors.yellow },
              { icon: <Search size={24} />, text: 'Identificação de lacunas de aprendizagem', color: colors.red },
              { icon: <Users size={24} />, text: 'Acompanhamento por escola, turma e aluno', color: colors.navy },
              { icon: <Target size={24} />, text: 'Subsídio técnico à tomada de decisão pedagógica', color: colors.blue },
            ].map((item, i) => (
              <motion.div
                key={i}
                className={`p-6 rounded-2xl border flex items-start gap-4 transition-all duration-300 ${cardBg} ${cardHover}`}
                variants={staggerItem}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <motion.div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: item.color + '15', color: item.color }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  {item.icon}
                </motion.div>
                <p className={`font-semibold ${text} pt-2`}>{item.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>


      {/* ===== COMPONENTES DO SISTEMA ===== */}
      <section id="componentes" className={`py-20 px-6 ${bg}`}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: colors.green }}>
              Componentes do Sistema
            </p>
            <h2 className="text-3xl sm:text-4xl font-black">
              Módulos Integrados para Gestão Completa
            </h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {sistemComponents.map((card, i) => (
              <motion.div
                key={i}
                className={`p-8 rounded-2xl border transition-all duration-300 backdrop-blur-sm group ${cardBg} ${cardHover}`}
                variants={staggerItem}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
              >
                <motion.div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: card.color + '15', color: card.color }}
                  whileHover={{ scale: 1.15, rotate: -5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  {card.icon}
                </motion.div>
                <h3 className="text-xl font-black mb-3">{card.title}</h3>
                <p className={`${textSubtle} font-medium leading-relaxed`}>{card.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== FUNCIONALIDADES TÉCNICAS ===== */}
      <section id="funcionalidades" className={`py-20 px-6 ${sectionAlt} relative overflow-hidden`}>
        {/* DotGrid Background */}
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <DotGrid
            dotSize={4}
            gap={32}
            proximity={110}
            speedTrigger={200}
            shockRadius={70}
            shockStrength={7}
            returnDuration={2.2}
          />
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: colors.yellow }}>
              Funcionalidades Técnicas
            </p>
            <h2 className="text-3xl sm:text-4xl font-black">
              Recursos Avançados para Análise de Dados
            </h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {funcionalidadesTecnicas.map((item, i) => (
              <motion.div
                key={i}
                className={`p-5 rounded-xl border flex items-center gap-4 transition-all ${cardBg} ${cardHover}`}
                variants={staggerItem}
                whileHover={{ x: 10, transition: { duration: 0.2 } }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, type: "spring" }}
                >
                  <CheckCircle2 size={22} style={{ color: colors.green }} className="flex-shrink-0" />
                </motion.div>
                <span className="font-medium">{item}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== IMPLANTAÇÃO E SUPORTE ===== */}
      <section className={`py-20 px-6 ${bg}`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={slideInLeft}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: colors.green }}>
                Implantação e Suporte
              </p>
              <h2 className="text-3xl sm:text-4xl font-black mb-6">
                Acompanhamento Completo do Início ao Fim
              </h2>
              <p className={`${textSubtle} text-lg mb-8`}>
                Nossa equipe acompanha todo o processo de implantação, desde a configuração inicial até a capacitação das equipes.
              </p>
              <motion.div
                className="space-y-4"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {implantacao.map((item, i) => (
                  <motion.div
                    key={i}
                    className="flex items-center gap-4"
                    variants={staggerItem}
                    whileHover={{ x: 10 }}
                  >
                    <motion.div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                      style={{ background: colors.green }}
                      whileHover={{ scale: 1.2 }}
                    >
                      {i + 1}
                    </motion.div>
                    <span className="font-semibold">{item}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              className={`p-8 rounded-3xl border ${cardBg}`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={scaleIn}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -5 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <motion.div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center"
                  style={{ background: colors.green + '15' }}
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <HeadphonesIcon size={32} style={{ color: colors.green }} />
                </motion.div>
                <div>
                  <h3 className="text-xl font-black">Suporte Dedicado</h3>
                  <p className={textMuted}>Equipe pronta para ajudar</p>
                </div>
              </div>
              <p className={`${textSubtle} leading-relaxed`}>
                Conte com suporte técnico operacional, orientação de uso pedagógico e acompanhamento contínuo para garantir o máximo aproveitamento da plataforma.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      {/* ===== SOBRE O PROGRAMA ===== */}
      <section id="sobre" className={`py-20 px-6 ${sectionAlt} relative overflow-hidden`}>
        {/* DotGrid Background */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <DotGrid
            dotSize={4}
            gap={30}
            proximity={120}
            speedTrigger={200}
            shockRadius={80}
            shockStrength={8}
            returnDuration={2}
          />
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: colors.blue }}>
              Sobre o Programa
            </p>
            <h2 className="text-3xl sm:text-4xl font-black mb-6">
              Conheça o SIDEB+
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={staggerContainer}
            >
              <motion.div
                className={`p-8 rounded-3xl border mb-8 ${cardBg}`}
                variants={staggerItem}
                whileHover={{ y: -5 }}
              >
                <h3 className="text-xl font-black mb-4" style={{ color: colors.blue }}>O que é o SIDEB+</h3>
                <p className={`${textSubtle} leading-relaxed`}>
                  Trata-se de uma plataforma tecnológica integrada que organiza, consolida e analisa dados educacionais provenientes de avaliações, frequência e desempenho acadêmico, permitindo visualização estruturada por aluno, turma, escola e rede.
                </p>
              </motion.div>

              <motion.div
                className={`p-8 rounded-3xl border ${cardBg}`}
                variants={staggerItem}
                whileHover={{ y: -5 }}
              >
                <h3 className="text-xl font-black mb-4" style={{ color: colors.green }}>Natureza da Solução</h3>
                <p className={`${textSubtle} leading-relaxed`}>
                  O SIDEB+ é um instrumento de apoio técnico à gestão educacional. Não substitui a avaliação oficial, mas fornece monitoramento contínuo, diagnóstico estruturado e evidências operacionais para orientar ações pedagógicas e administrativas.
                </p>
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={slideInRight}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-xl font-black mb-6" style={{ color: colors.navy }}>Objetivo do Programa</h3>
              <motion.div
                className="space-y-3 mb-8"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {[
                  'Estruturar o monitoramento contínuo do desempenho escolar',
                  'Fornecer diagnósticos pedagógicos baseados em dados',
                  'Identificar lacunas de aprendizagem por habilidade',
                  'Apoiar o planejamento de intervenções educacionais',
                  'Subsidiar decisões técnicas da gestão da rede',
                  'Padronizar instrumentos de avaliação diagnóstica',
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    className={`p-4 rounded-xl border flex items-center gap-3 ${cardBg}`}
                    variants={staggerItem}
                    whileHover={{ x: 5 }}
                  >
                    <CheckCircle2 size={20} style={{ color: colors.navy }} />
                    <span className="font-medium">{item}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>

          {/* Como Opera */}
          <motion.div
            className={`mt-12 p-8 rounded-3xl border ${cardBg}`}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-xl font-black mb-6 text-center" style={{ color: colors.yellow }}>Como o Programa Opera</h3>
            <p className={`${textSubtle} text-center mb-8 max-w-3xl mx-auto`}>
              O programa funciona por meio da aplicação de avaliações diagnósticas, processamento automatizado de resultados e disponibilização de painéis gerenciais.
            </p>
            <motion.div
              className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {[
                { icon: <ClipboardCheck size={24} />, text: 'Aplicação de instrumentos avaliativos' },
                { icon: <Settings size={24} />, text: 'Processamento e consolidação' },
                { icon: <Brain size={24} />, text: 'Análise por habilidade' },
                { icon: <FileText size={24} />, text: 'Geração de relatórios' },
                { icon: <BarChart3 size={24} />, text: 'Visualização em dashboards' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="text-center p-4"
                  variants={staggerItem}
                  whileHover={{ y: -5 }}
                >
                  <motion.div
                    className="w-12 h-12 rounded-xl mx-auto flex items-center justify-center mb-3"
                    style={{ background: colors.yellow + '15', color: colors.yellow }}
                    whileHover={{ scale: 1.2, rotate: 10 }}
                  >
                    {item.icon}
                  </motion.div>
                  <p className="text-sm font-semibold">{item.text}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* ===== PÚBLICO ALVO ===== */}
      <section className={`py-20 px-6 ${bg} relative overflow-hidden`}>
        {/* DotGrid Background */}
        <div className="absolute inset-0 opacity-15 pointer-events-none">
          <DotGrid
            dotSize={5}
            gap={28}
            proximity={100}
            speedTrigger={180}
            shockRadius={90}
            shockStrength={6}
            returnDuration={1.8}
          />
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-black uppercase tracking-widest mb-3" style={{ color: colors.red }}>
              Público de Aplicação
            </p>
            <h2 className="text-3xl sm:text-4xl font-black">
              Para Quem é o SIDEB+
            </h2>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {publicoAlvo.map((item, i) => (
              <motion.div
                key={i}
                className={`p-6 rounded-2xl border text-center transition-all duration-300 group ${cardBg} ${cardHover}`}
                variants={staggerItem}
                whileHover={{ y: -10, transition: { duration: 0.2 } }}
              >
                <motion.div
                  className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4"
                  style={{ background: colors.red + '15', color: colors.red }}
                  whileHover={{ scale: 1.15, rotate: -5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  {item.icon}
                </motion.div>
                <p className="font-semibold">{item.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <motion.section
        className="py-20 px-6 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, #0974BA, #2E4974)` }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        transition={{ duration: 0.8 }}
      >
        {/* DotGrid Background */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <DotGrid
            dotSize={4}
            gap={22}
            proximity={130}
            speedTrigger={160}
            shockRadius={100}
            shockStrength={8}
            returnDuration={2}
          />
        </div>
        {/* Background animated circles */}
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'white' }}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, -30, 0]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-10"
          style={{ background: 'white' }}
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -30, 0],
            y: [0, 20, 0]
          }}
          transition={{ duration: 6, repeat: Infinity }}
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.h2
            className="text-3xl sm:text-4xl font-black text-white mb-6"
            variants={fadeInUp}
          >
            Pronto para Transformar a Gestão Educacional?
          </motion.h2>
          <motion.p
            className="text-white/80 text-lg mb-8 max-w-2xl mx-auto"
            variants={fadeInUp}
          >
            Junte-se a centenas de redes de ensino que já utilizam o SIDEB+ para monitorar e melhorar seus indicadores educacionais.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            variants={scaleIn}
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/login')}
              className="px-10 py-4 rounded-full bg-white font-bold text-lg flex items-center gap-2 shadow-xl transition"
              style={{ color: colors.blue }}
            >
              Começar Agora
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ArrowRight size={20} />
              </motion.span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="px-10 py-4 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/40 font-bold text-lg flex items-center gap-2 text-white shadow-xl transition"
            >
              Ver Demo
              <BarChart3 size={20} />
            </motion.button>
          </motion.div>
        </div>
      </motion.section>

      {/* ===== FOOTER ===== */}
      <footer className={`border-t py-12 px-6 ${divider} ${bg}`}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="flex flex-col md:flex-row items-center justify-between gap-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="flex items-center gap-3"
              whileHover={{ scale: 1.02 }}
            >
              <img src="/iconSIDEB.svg" alt="SIDEB+" className="h-10" />
              <div>
                <span className="font-black text-lg">SIDEB<span style={{ color: colors.blue }}>+</span></span>
                <p className={`text-xs ${textMuted}`}>Sistema de Apoio à Gestão Educacional</p>
              </div>
            </motion.div>

            <div className="text-center md:text-right">
              <p className={`text-sm ${textMuted}`}>
                &copy; {new Date().getFullYear()} SIDEB+. Todos os direitos reservados.
              </p>
              <motion.p
                className={`text-sm font-semibold mt-1 ${textSubtle}`}
                whileHover={{ scale: 1.02 }}
              >
                Desenvolvido por{' '}
                <span className="font-black" style={{ color: colors.navy }}>BzR Tech</span>
              </motion.p>
            </div>
          </motion.div>
        </div>
      </footer>
    </div>
  );
}
